import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import {
  citHasSalesConfirmationAttachment,
  updateCit,
  uploadCitAttachment,
} from './citSubmissions';
import {
  CIT_STATUS_OPTIONS,
  createCitDocumentRow,
  type ApplicationSubmission,
  type CitTransferStatus,
  type CustomiseItemsTransferFormData,
} from '../types/applicationForm';

export type CitBulkSheetRow = {
  scNo: string;
  needsDeposit: boolean | null;
  status: CitTransferStatus | null;
  sourceRow: number;
};

export type CitBulkAttachPlan = {
  submission: ApplicationSubmission;
  scNo: string;
  file?: File;
  needsDeposit: boolean | null;
  status: CitTransferStatus | null;
};

export type CitBulkAttachPreview = {
  plans: CitBulkAttachPlan[];
  missingCount: number;
  errors: string[];
  warnings: string[];
};

export type CitBulkAttachResult = {
  updated: number;
  skipped: number;
  errors: string[];
};

export function citScMatchKey(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function citMissingScAttachmentRows(
  rows: ApplicationSubmission[],
): ApplicationSubmission[] {
  return rows.filter((row) => {
    const data = row.form_data as CustomiseItemsTransferFormData;
    return Boolean(data.sales_confirmation_no?.trim()) && !citHasSalesConfirmationAttachment(data);
  });
}

function cellToString(value: ExcelJS.CellValue): string {
  if (value == null) return '';
  if (typeof value === 'object' && value !== null && 'text' in value) {
    return String((value as { text?: string }).text ?? '').trim();
  }
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).trim();
}

function normalizeColName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function pickColumn(columns: string[], options: string[]): string | null {
  const normalized = new Map(columns.map((c) => [normalizeColName(c), c]));
  for (const opt of options) {
    const hit = normalized.get(normalizeColName(opt));
    if (hit) return hit;
  }
  return null;
}

function parseNeedsDeposit(raw: string): boolean | null | 'invalid' {
  const key = raw.trim().toLowerCase();
  if (!key) return null;
  if (['yes', 'y', 'true', '1'].includes(key)) return true;
  if (['no', 'n', 'false', '0'].includes(key)) return false;
  return 'invalid';
}

function parseStatus(raw: string): CitTransferStatus | null | 'invalid' {
  const key = raw.trim().toLowerCase();
  if (!key) return null;
  const hit = CIT_STATUS_OPTIONS.find((option) => option.toLowerCase() === key);
  return hit ?? 'invalid';
}

async function rowsFromSpreadsheet(file: File): Promise<Record<string, string>[]> {
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.csv')) {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
    return lines.slice(1).map((line, index) => {
      const cells = line.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
      const row: Record<string, string> = { __row: String(index + 2) };
      headers.forEach((h, i) => {
        row[h] = cells[i] ?? '';
      });
      return row;
    });
  }

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets[0];
  if (!sheet) return [];
  const headers: string[] = [];
  sheet.getRow(1).eachCell((cell, col) => {
    headers[col - 1] = cellToString(cell.value);
  });
  const rows: Record<string, string>[] = [];
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const record: Record<string, string> = { __row: String(rowNumber) };
    row.eachCell((cell, col) => {
      const header = headers[col - 1];
      if (!header) return;
      record[header] = cellToString(cell.value);
    });
    if (Object.entries(record).some(([k, v]) => k !== '__row' && v)) rows.push(record);
  });
  return rows;
}

export async function parseCitBulkSheet(file: File): Promise<{
  rows: CitBulkSheetRow[];
  errors: string[];
}> {
  const df = await rowsFromSpreadsheet(file);
  const errors: string[] = [];
  if (!df.length) return { rows: [], errors: ['Spreadsheet has no data rows.'] };

  const columns = Object.keys(df[0]).filter((k) => k !== '__row');
  const colSc = pickColumn(columns, [
    'SC No',
    'Sales confirmation no',
    'sales confirmation no.',
    'sales_confirmation_no',
    'soc no',
  ]);
  if (!colSc) return { rows: [], errors: ['Missing column: Sales confirmation no'] };

  const colArr = pickColumn(columns, [
    'Deposit arrangement',
    'Deposit arrangement?',
    'Need deposit arrangement',
    'needs_deposit',
    'Needs deposit',
  ]);
  const colStatus = pickColumn(columns, ['Status', 'Document status']);

  const rows: CitBulkSheetRow[] = [];
  const seen = new Map<string, number>();
  for (const row of df) {
    const sourceRow = Number(row.__row) || 0;
    const scNo = (row[colSc] ?? '').trim();
    if (!scNo) continue;
    const key = citScMatchKey(scNo);
    if (!key) continue;
    if (seen.has(key)) {
      errors.push(`Row ${sourceRow}: duplicate SC ${scNo} (also row ${seen.get(key)}).`);
      continue;
    }
    seen.set(key, sourceRow);

    const needsDeposit = colArr ? parseNeedsDeposit(row[colArr] ?? '') : null;
    if (needsDeposit === 'invalid') {
      errors.push(`Row ${sourceRow}: Deposit arrangement must be Yes or No.`);
      continue;
    }
    const status = colStatus ? parseStatus(row[colStatus] ?? '') : null;
    if (status === 'invalid') {
      errors.push(
        `Row ${sourceRow}: Status must be ${CIT_STATUS_OPTIONS.join(', ')}.`,
      );
      continue;
    }

    rows.push({
      scNo,
      needsDeposit,
      status,
      sourceRow,
    });
  }

  return { rows, errors };
}

function fileStem(name: string): string {
  return name.replace(/^.*[/\\]/, '').replace(/\.[^.]+$/, '');
}

export async function collectCitBulkPdfFiles(files: File[]): Promise<{
  filesByKey: Map<string, File>;
  warnings: string[];
}> {
  const filesByKey = new Map<string, File>();
  const warnings: string[] = [];

  async function addPdf(file: File, label: string) {
    const key = citScMatchKey(fileStem(label));
    if (!key) {
      warnings.push(`Skipped file with no SC-like name: ${label}`);
      return;
    }
    const existing = filesByKey.get(key);
    if (existing) {
      warnings.push(`Multiple PDFs for ${key}; using ${existing.name}, skipped ${label}.`);
      return;
    }
    filesByKey.set(key, file);
  }

  for (const file of files) {
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.pdf') || file.type === 'application/pdf') {
      await addPdf(file, file.name);
      continue;
    }
    if (!lower.endsWith('.zip')) continue;
    const zip = await JSZip.loadAsync(await file.arrayBuffer());
    const entries = Object.values(zip.files);
    for (const entry of entries) {
      if (entry.dir) continue;
      const name = entry.name.replace(/^.*[/\\]/, '');
      if (name.startsWith('.') || entry.name.includes('__MACOSX')) continue;
      if (!name.toLowerCase().endsWith('.pdf')) continue;
      const blob = await entry.async('blob');
      const pdf = new File([blob], name, { type: 'application/pdf' });
      await addPdf(pdf, name);
    }
  }

  return { filesByKey, warnings };
}

export function buildCitBulkAttachPreview(
  allRows: ApplicationSubmission[],
  sheetRows: CitBulkSheetRow[],
  filesByKey: Map<string, File>,
): CitBulkAttachPreview {
  const errors: string[] = [];
  const warnings: string[] = [];
  const missing = citMissingScAttachmentRows(allRows);
  const missingByKey = new Map<string, ApplicationSubmission>();
  for (const row of missing) {
    const data = row.form_data as CustomiseItemsTransferFormData;
    const key = citScMatchKey(data.sales_confirmation_no);
    if (!key) continue;
    missingByKey.set(key, row);
  }

  const sheetByKey = new Map<string, CitBulkSheetRow>();
  for (const row of sheetRows) {
    const key = citScMatchKey(row.scNo);
    sheetByKey.set(key, row);
    if (!missingByKey.has(key)) {
      const already = allRows.find(
        (item) =>
          citScMatchKey(
            (item.form_data as CustomiseItemsTransferFormData).sales_confirmation_no ?? '',
          ) === key,
      );
      if (already && citHasSalesConfirmationAttachment(already.form_data as CustomiseItemsTransferFormData)) {
        warnings.push(`${row.scNo}: already has SC file — skipped.`);
      } else {
        errors.push(`${row.scNo}: no CIT row without SC file.`);
      }
    }
  }

  for (const key of filesByKey.keys()) {
    if (!missingByKey.has(key)) {
      warnings.push(`${filesByKey.get(key)?.name}: no matching row without SC file.`);
    }
  }

  const plans: CitBulkAttachPlan[] = [];
  for (const [key, submission] of missingByKey) {
    const sheet = sheetByKey.get(key);
    const file = filesByKey.get(key);
    if (!sheet && !file) continue;
    const data = submission.form_data as CustomiseItemsTransferFormData;
    plans.push({
      submission,
      scNo: data.sales_confirmation_no.trim(),
      file,
      needsDeposit: sheet?.needsDeposit ?? null,
      status: sheet?.status ?? null,
    });
  }

  plans.sort((a, b) => a.scNo.localeCompare(b.scNo));
  return { plans, missingCount: missing.length, errors, warnings };
}

export async function downloadCitBulkAttachTemplate(
  rows: ApplicationSubmission[],
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('CIT bulk fill');
  sheet.addRow(['Sales confirmation no', 'Deposit arrangement', 'Status']);
  sheet.getRow(1).font = { bold: true };
  for (const row of citMissingScAttachmentRows(rows)) {
    const data = row.form_data as CustomiseItemsTransferFormData;
    sheet.addRow([data.sales_confirmation_no.trim(), '', '']);
  }
  sheet.getColumn(1).width = 24;
  sheet.getColumn(2).width = 22;
  sheet.getColumn(3).width = 16;
  const note = workbook.addWorksheet('Notes');
  note.addRow(['Temporary CIT bulk fill — only rows that do not yet have an SC PDF.']);
  note.addRow(['Deposit arrangement: Yes or No (blank = leave unchanged).']);
  note.addRow(['Status: New, Processing, Completed, Cancelled (blank = auto from arrangement).']);
  note.addRow(['PDF file names should match the SC no, e.g. 2026SOC10003.pdf for 2026/SOC/10/003.']);
  const buf = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'cit-bulk-fill-no-sc-file.xlsx';
  a.click();
  URL.revokeObjectURL(url);
}

export async function runCitBulkAttachImport(
  plans: CitBulkAttachPlan[],
  onProgress?: (done: number, total: number) => void,
): Promise<CitBulkAttachResult> {
  let updated = 0;
  let skipped = 0;
  const errors: string[] = [];
  const total = plans.length;

  for (let i = 0; i < plans.length; i += 1) {
    const plan = plans[i];
    onProgress?.(i, total);
    const current = plan.submission.form_data as CustomiseItemsTransferFormData;
    if (citHasSalesConfirmationAttachment(current)) {
      skipped += 1;
      continue;
    }
    if (!plan.file && plan.needsDeposit == null && !plan.status) {
      skipped += 1;
      continue;
    }

    try {
      let next: CustomiseItemsTransferFormData = { ...current };
      if (plan.file) {
        next = {
          ...next,
          sales_confirmation_file: await uploadCitAttachment(
            plan.submission.id,
            'sales_confirmation',
            plan.file,
          ),
        };
      }
      if (plan.needsDeposit != null) {
        next = {
          ...next,
          needs_deposit: plan.needsDeposit,
          deposit_invoices:
            plan.needsDeposit === true && next.deposit_invoices.length === 0
              ? [createCitDocumentRow()]
              : next.deposit_invoices,
        };
      }
      if (plan.status) {
        next = { ...next, status: plan.status };
      }

      if (plan.status) {
        // Honour sheet status; No still becomes Completed unless Cancelled.
        if (next.needs_deposit === false && next.status !== 'Cancelled') {
          next = { ...next, status: 'Completed' };
        }
        await updateCit(plan.submission.id, next, { skipAutoStatus: true });
      } else {
        await updateCit(plan.submission.id, next);
      }
      updated += 1;
    } catch (err) {
      errors.push(
        `${plan.scNo}: ${err instanceof Error ? err.message : 'Update failed.'}`,
      );
    }
  }

  onProgress?.(total, total);
  return { updated, skipped, errors };
}
